import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AutoCloudDnsClient } from '../../utils/auto-cloud-dns-client';

/**
 * Interface representing a domain.
 */
export interface Idomain {
  id: number;
  name: string;
  domain: string;
  webIp: string;
  mailIp: string;
  zoneId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Interface representing a subdomain.
 */
export interface Isubdomain {
  subdomain: string;
  ipAddress: string;
  proxied: string;
}

/**
 * Class representing a domain.
 */
export class AutoDnsDomain {
  /**
   * Base URL for domain API endpoints.
   */
  static baseUrl = '/domains';

  /**
   * The ID of the domain.
   */
  id: number;

  /**
   * The name of the domain.
   */
  name: string;

  /**
   * The domain name.
   */
  domain: string;

  /**
   * The web IP address.
   */
  webIp: string;

  /**
   * The mail IP address.
   */
  mailIp: string;

  /**
   * The zone ID.
   */
  zoneId: string;

  /**
   * The creation date.
   */
  createdAt: string;

  /**
   * The update date.
   */
  updatedAt: string;

  constructor(data: Idomain) {
    this.id = data.id;
    this.name = data.name;
    this.domain = data.domain;
    this.webIp = data.webIp;
    this.mailIp = data.mailIp;
    this.zoneId = data.zoneId;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  /**
   * Retrieves all domains.
   *
   * @returns A Promise that resolves to an array of AutoDnsDomain instances.
   */
  static async getAll() {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<Idomain[]>(`${AutoDnsDomain.baseUrl}`);
    const data = await lastValueFrom(observer$);

    return data.map(val => new AutoDnsDomain(val)) ;
  }

  /**
   * Retrieves DNS records.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static records() {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<Isuccess>(`${AutoDnsDomain.baseUrl}/dns-records`);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves analytics.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static analytics() {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<Isuccess>(`${AutoDnsDomain.baseUrl}/analytics`);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves a specific domain.
   *
   * @param resourceId - The ID of the domain to retrieve.
   * @returns A Promise that resolves to a new AutoDnsDomain instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<Idomain>(`${AutoDnsDomain.baseUrl}/${resourceId}`);
    const domain = await lastValueFrom(observer$);

    return new AutoDnsDomain(domain);
  }

  /**
   * Adds a subdomain.
   *
   * @param payload - The payload containing the subdomain information.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static addSubDomain(payload: Isubdomain) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makePost<Isuccess>(`${AutoDnsDomain.baseUrl}/subdomain`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes a subdomain.
   *
   * @param subdomain - The subdomain to remove.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  removeSubDomain(subdomain: string) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeDelete<Isuccess>(`${AutoDnsDomain.baseUrl}/subdomain/${subdomain}`);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the domain.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeDelete<Isuccess>(`${AutoDnsDomain.baseUrl}/${this.id}`);

    return lastValueFrom(observer$);
  }
}
